import { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ReactiveDialog from "components/common/ReactiveDialog";
import { useSearchParams } from "react-router-dom";
import { useTypewriter } from "components/hooks/useTypewriter";
import { LEARNING_STEPS } from "components/member/learningSteps";

const ExampleTypewriter = ({ examples }: { examples: string[] }) => {
  const text = useTypewriter({
    texts: examples,
    enabled: true,
    typingSpeed: 30,
    erasingSpeed: 15,
    pauseDuration: 2500,
  });

  return (
    <Box
      sx={{
        backgroundColor: "#F9FAFB",
        border: "1px solid #EAECF0",
        borderRadius: "12px",
        padding: "14px 16px",
        minHeight: "48px",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Typography variant="body" sx={{ color: "#344054", fontStyle: "italic" }}>
        &ldquo;{text}&rdquo;
        <Box
          component="span"
          sx={{
            display: "inline-block",
            width: "2px",
            height: "1em",
            backgroundColor: "#198282",
            marginLeft: "1px",
            verticalAlign: "text-bottom",
            animation: "blink 1s step-end infinite",
            "@keyframes blink": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0 },
            },
          }}
        />
      </Typography>
    </Box>
  );
};

export default () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeStep, setActiveStep] = useState(0);
  const open = searchParams.get("from") === "signup";
  const step = LEARNING_STEPS[activeStep];

  const handleClose = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("from");
    setSearchParams(nextParams, { replace: true });
    setActiveStep(0);
  };

  return (
    <ReactiveDialog
      open={open}
      onClose={handleClose}
      title="Quick tour of your Faye agent"
      fullWidthSize="md"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          paddingTop: "4px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <Box
              sx={{
                width: "56px",
                height: "56px",
                borderRadius: "18px",
                backgroundColor: step.accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <step.Icon size={28} color="#198282" />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography variant="h3">{step.title}</Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography variant="body">{step.description}</Typography>
            <Typography variant="body">{step.helper}</Typography>
          </Box>

          <ExampleTypewriter key={step.key} examples={step.examples} />
        </Box>
        <Box
          sx={{ display: "flex", gap: "10px", justifyContent: "space-between" }}
        >
          {LEARNING_STEPS.map((item, index) => {
            const isActive = index === activeStep;
            const isComplete = index < activeStep;
            return (
              <Box
                key={item.key}
                sx={{
                  flex: 1,
                  height: "6px",
                  borderRadius: "999px",
                  backgroundColor:
                    isActive || isComplete ? "#198282" : "#E4E7EC",
                  opacity: isActive ? 1 : isComplete ? 0.7 : 1,
                  transition: "background-color 0.2s ease",
                }}
              />
            );
          })}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <Button
            variant="text"
            disabled={activeStep === 0}
            onClick={() => setActiveStep((step) => Math.max(0, step - 1))}
            startIcon={<ChevronLeft size={18} />}
            sx={{ textTransform: "none" }}
          >
            Back
          </Button>

          {activeStep < LEARNING_STEPS.length - 1 ? (
            <Button
              variant="contained"
              onClick={() =>
                setActiveStep((step) =>
                  Math.min(LEARNING_STEPS.length - 1, step + 1),
                )
              }
              endIcon={<ChevronRight size={18} />}
              sx={{ textTransform: "none" }}
            >
              Next
            </Button>
          ) : (
            <Button variant="contained" onClick={handleClose}>
              Finish
            </Button>
          )}
        </Box>
      </Box>
    </ReactiveDialog>
  );
};
